import React, { useCallback, useMemo, useState, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { AnimatePresence, motion } from 'framer-motion'
import {
  useFloating,
  useDismiss,
  useInteractions,
  flip,
  shift,
  offset,
  autoUpdate,
  type VirtualElement,
} from '@floating-ui/react'
import { Copy, Trash2 } from 'lucide-react'
import {
  TaskStartIcon,
  TaskDeadlineIcon,
  TaskAttentionIcon,
  DurationIcon,
  TagIcon,
  MotivationIcon,
} from '@/src/renderer/components/icons'
import { DormantIcon } from '@/src/renderer/components/icons/DormantIcon'
import { ReactivateIcon } from '@/src/renderer/components/icons/ReactivateIcon'
import { TaskDropdown } from '../TaskDropdown'
import { WhenDropdownContent, type StartValue } from '../WhenDropdown'
import { DeadlineDropdownContent, type DeadlineValue } from '../DeadlineDropdown'
import { DurationDropdownContent } from '../DurationDropdown'
import { AttentionDropdownContent } from '../AttentionDropdown'
import { TagDropdownContent } from '../TagDropdown'
import type { DurationValue } from '../TaskEntry'
import type { TaskContextMenuProps } from './TaskContextMenu.types'
import './TaskContextMenu.scss'

const TITLEBAR_HEIGHT = 48
const VIEWPORT_PADDING = 8

const COLLISION_PADDING = {
  top: TITLEBAR_HEIGHT + VIEWPORT_PADDING,
  bottom: VIEWPORT_PADDING,
  left: VIEWPORT_PADDING,
  right: VIEWPORT_PADDING,
}

const badgeEnter = {
  width: 32,
  opacity: 1,
  scale: 1,
  transition: {
    width: { duration: 0.15, ease: 'easeInOut' as const },
    opacity: { duration: 0.2, ease: 'easeInOut' as const, delay: 0.05 },
    scale: { duration: 0.2, ease: 'easeInOut' as const, delay: 0.05 },
  },
}

const badgeExit = {
  opacity: 0,
  scale: 0.7,
  width: 0,
  transition: {
    opacity: { duration: 0.2, ease: 'easeInOut' as const },
    scale: { duration: 0.2, ease: 'easeInOut' as const },
    width: { duration: 0.15, ease: 'easeInOut' as const, delay: 0.1 },
  },
}

const badgeInitial = { width: 0, opacity: 0, scale: 0.7 }

/** Shared wrapper for one collapsible badge slot: animates in/out and hosts a TaskDropdown. */
interface BadgeSlotProps {
  visible: boolean
  slotKey: string
  open: boolean
  onOpenChange: (open: boolean) => void
  icon: React.ReactNode
  title: string
  reserveBottom?: number
  /** Hover handoff: fired when the wrapper is entered. The toolbar
   *  uses it to switch the open dropdown to whichever badge the
   *  pointer enters next — so the user can sweep across slots without
   *  having to close-then-open. */
  onMouseEnter?: () => void
  children: React.ReactNode
}

const BadgeSlot: React.FC<BadgeSlotProps> = ({
  visible,
  slotKey,
  open,
  onOpenChange,
  icon,
  title,
  reserveBottom,
  onMouseEnter,
  children,
}) => (
  <AnimatePresence initial={false}>
    {visible && (
      <motion.div
        key={slotKey}
        className="task-context-menu__badge-wrap"
        initial={badgeInitial}
        animate={badgeEnter}
        exit={badgeExit}
        onMouseEnter={onMouseEnter}
      >
        <TaskDropdown
          open={open}
          onOpenChange={onOpenChange}
          side="bottom"
          align="center"
          sideOffset={6}
          reserveBottom={reserveBottom}
          trigger={
            <button
              type="button"
              className={`task-context-menu__icon-button${open ? ' task-context-menu__icon-button--dropdown-open' : ''}`}
              title={title}
              aria-label={title}
            >
              {icon}
            </button>
          }
        >
          {children}
        </TaskDropdown>
      </motion.div>
    )}
  </AnimatePresence>
)

export const TaskContextMenu: React.FC<TaskContextMenuProps> = ({
  open,
  onOpenChange,
  anchorPoint,
  task,
  onStartChange,
  onDurationChange,
  onDeadlineChange,
  onAttentionChange,
  onTagsChange,
  onPause,
  onReactivate,
  onDuplicate,
  onDelete,
  onSubDropdownOpenChange,
}) => {
  const { t } = useTranslation()

  // Sub-dropdown open states
  const [startOpen, setStartOpen] = useState(false)
  const [durationOpen, setDurationOpen] = useState(false)
  const [deadlineOpen, setDeadlineOpen] = useState(false)
  const [attentionOpen, setAttentionOpen] = useState(false)
  const [tagsOpen, setTagsOpen] = useState(false)

  const anySubOpen = startOpen || durationOpen || deadlineOpen || attentionOpen || tagsOpen

  // Hover handoff: when one sub is open and the pointer enters another
  // badge, swap the open one over. Mirrors the actionbar in TaskEntry's
  // expanded mode so users can sweep across slots without click-out-
  // click-in. No-op unless something is already open — mouse-enter on
  // an idle badge mustn't pop a dropdown out of nowhere.
  const switchSub = useCallback((target: 'start' | 'duration' | 'deadline' | 'attention' | 'tags') => {
    if (!anySubOpen) return
    setStartOpen(target === 'start')
    setDurationOpen(target === 'duration')
    setDeadlineOpen(target === 'deadline')
    setAttentionOpen(target === 'attention')
    setTagsOpen(target === 'tags')
  }, [anySubOpen])

  // Notify parent on sub-dropdown state change (for auto-select)
  useEffect(() => {
    onSubDropdownOpenChange?.(anySubOpen)
  }, [anySubOpen, onSubDropdownOpenChange])

  // Close all sub-dropdowns when the menu itself closes
  useEffect(() => {
    if (!open) {
      setStartOpen(false)
      setDurationOpen(false)
      setDeadlineOpen(false)
      setAttentionOpen(false)
      setTagsOpen(false)
    }
  }, [open])

  // Virtual reference element at the anchor point
  const virtualElement = useMemo<VirtualElement | null>(() => {
    if (!anchorPoint) return null
    const { x, y } = anchorPoint
    return {
      getBoundingClientRect: () => ({
        x, y, width: 0, height: 0,
        top: y, left: x, right: x, bottom: y,
        toJSON: () => ({}),
      }),
    }
  }, [anchorPoint])

  const middleware = useMemo(
    () => [
      // mainAxis: -2 → overlap the task row by 2px vertically.
      // No crossAxis offset — `placement: 'top'` already centres the
      // menu on the click point.
      offset({ mainAxis: -2 }),
      flip({ padding: COLLISION_PADDING, fallbackPlacements: ['bottom'] }),
      shift({ padding: COLLISION_PADDING }),
    ],
    [],
  )

  const { refs, floatingStyles, context, isPositioned } = useFloating({
    open,
    onOpenChange,
    placement: 'top',
    whileElementsMounted: autoUpdate,
    middleware,
    elements: { reference: virtualElement },
  })

  const dismiss = useDismiss(context, {
    outsidePress: (event) => {
      const target = event.target as Element | null
      if (!target) return true
      // Ignore clicks inside any nested TaskDropdown (sub-dropdown) so the
      // context menu stays open while the user interacts with a picker.
      if (target.closest('[data-task-dropdown]')) return false
      if (target.closest('[data-task-dropdown-backdrop]')) return false
      return true
    },
    escapeKey: true,
  })

  const { getFloatingProps } = useInteractions([dismiss])

  // ── "Is set" checks (cheap scalar comparisons, no memo needed) ──
  const hasStart = task.startDate != null
  const hasDuration = task.estimatedDuration != null
  const hasDeadline = task.dueDate != null
  const hasAttention = task.attention != null
  const hasTags = (task.tags?.length ?? 0) > 0

  const startValue: StartValue | null = useMemo(
    () => (task.startDate
      ? { date: task.startDate, time: task.startTime ?? null, period: task.startPeriod ?? null }
      : null),
    [task.startDate, task.startTime, task.startPeriod],
  )

  const deadlineValue: DeadlineValue | null = useMemo(
    () => (task.dueDate
      ? { date: task.dueDate, time: task.dueTime ?? null, period: task.duePeriod ?? null }
      : null),
    [task.dueDate, task.dueTime, task.duePeriod],
  )

  const durationValue: DurationValue | null = useMemo(
    () => (task.estimatedDuration
      ? { minutes: task.estimatedDuration, mode: task.durationMode ?? 'duration' }
      : null),
    [task.estimatedDuration, task.durationMode],
  )

  const handlePauseClick = useCallback(() => {
    onPause()
    onOpenChange(false)
  }, [onPause, onOpenChange])

  const handleReactivateClick = useCallback(() => {
    onReactivate()
    onOpenChange(false)
  }, [onReactivate, onOpenChange])

  const handleDuplicateClick = useCallback(() => {
    onDuplicate()
    onOpenChange(false)
  }, [onDuplicate, onOpenChange])

  const handleDeleteClick = useCallback(() => {
    onDelete()
    onOpenChange(false)
  }, [onDelete, onOpenChange])

  if (!open || !anchorPoint) return null

  return createPortal(
    <div
      ref={refs.setFloating}
      data-task-context-menu
      data-task-dropdown
      className="task-context-menu"
      style={{
        ...floatingStyles,
        zIndex: 50,
        visibility: isPositioned ? 'visible' : 'hidden',
        animation: isPositioned ? 'task-dropdown-fade-in 0.15s ease-out' : 'none',
      }}
      onClick={(e) => e.stopPropagation()}
      onDoubleClick={(e) => e.stopPropagation()}
      onContextMenu={(e) => e.preventDefault()}
      {...getFloatingProps()}
    >
      <div className="task-context-menu__toolbar">
        <BadgeSlot
          visible={!hasStart}
          slotKey="start"
          open={startOpen}
          onOpenChange={setStartOpen}
          icon={<TaskStartIcon size={16} />}
          title={t('taskCreation.actionStart')}
          reserveBottom={52}
          onMouseEnter={() => switchSub('start')}
        >
          <WhenDropdownContent
            value={startValue}
            onChange={(value) => { onStartChange(value); onOpenChange(false) }}
            onClose={() => setStartOpen(false)}
            variant="actionbar"
          />
        </BadgeSlot>

        <BadgeSlot
          visible={!hasDuration}
          slotKey="duration"
          open={durationOpen}
          onOpenChange={setDurationOpen}
          icon={<DurationIcon size={16} />}
          title={t('taskCreation.actionDuration')}
          onMouseEnter={() => switchSub('duration')}
        >
          <DurationDropdownContent
            value={durationValue}
            onChange={(value) => { onDurationChange(value); onOpenChange(false) }}
            onClose={() => setDurationOpen(false)}
            variant="actionbar"
          />
        </BadgeSlot>

        <BadgeSlot
          visible={!hasDeadline}
          slotKey="deadline"
          open={deadlineOpen}
          onOpenChange={setDeadlineOpen}
          icon={<TaskDeadlineIcon size={16} />}
          title={t('taskCreation.actionDeadline')}
          reserveBottom={52}
          onMouseEnter={() => switchSub('deadline')}
        >
          <DeadlineDropdownContent
            value={deadlineValue}
            onChange={(value) => { onDeadlineChange(value); onOpenChange(false) }}
            onClose={() => setDeadlineOpen(false)}
            variant="actionbar"
          />
        </BadgeSlot>

        <BadgeSlot
          visible={!hasAttention}
          slotKey="attention"
          open={attentionOpen}
          onOpenChange={setAttentionOpen}
          icon={<TaskAttentionIcon size={16} />}
          title={t('taskCreation.actionAttention')}
          onMouseEnter={() => switchSub('attention')}
        >
          <AttentionDropdownContent
            value={task.attention ?? null}
            onChange={(value) => { onAttentionChange(value); onOpenChange(false) }}
            onClose={() => setAttentionOpen(false)}
            variant="actionbar"
          />
        </BadgeSlot>

        <BadgeSlot
          visible={!hasTags}
          slotKey="tags"
          open={tagsOpen}
          onOpenChange={setTagsOpen}
          icon={<TagIcon size={16} />}
          title={t('taskCreation.actionTags')}
          reserveBottom={52}
          onMouseEnter={() => switchSub('tags')}
        >
          <TagDropdownContent
            selectedIds={task.tags ?? []}
            onChange={(value) => { onTagsChange(value); onOpenChange(false) }}
            onClose={() => setTagsOpen(false)}
            variant="actionbar"
          />
        </BadgeSlot>

        {/* Reward icon — feature not yet implemented; shown disabled until it ships. */}
        <button
          type="button"
          className="task-context-menu__icon-button task-context-menu__icon-button--disabled"
          title={t('taskCreation.actionReward')}
          aria-label={t('taskCreation.actionReward')}
          disabled
        >
          <MotivationIcon size={16} />
        </button>
      </div>

      {/* Separator is always rendered: the Reward icon is always visible. */}
      <div className="task-context-menu__separator" aria-hidden />

      {!task.dormant && (
        <button
          type="button"
          className="task-context-menu__icon-button"
          title={t('taskItem.pauseTask')}
          aria-label={t('taskItem.pauseTask')}
          onClick={handlePauseClick}
        >
          <DormantIcon size={16} />
        </button>
      )}
      {task.dormant && (
        <button
          type="button"
          className="task-context-menu__icon-button"
          title={t('taskItem.reactivateTask')}
          aria-label={t('taskItem.reactivateTask')}
          onClick={handleReactivateClick}
        >
          <ReactivateIcon size={16} />
        </button>
      )}
      <button
        type="button"
        className="task-context-menu__icon-button"
        title={t('taskItem.duplicateTask')}
        aria-label={t('taskItem.duplicateTask')}
        onClick={handleDuplicateClick}
      >
        <Copy size={16} strokeWidth={2} />
      </button>
      <button
        type="button"
        className="task-context-menu__icon-button task-context-menu__icon-button--delete"
        title={t('taskItem.deleteTask')}
        aria-label={t('taskItem.deleteTask')}
        onClick={handleDeleteClick}
      >
        <Trash2 size={16} strokeWidth={2} />
      </button>
    </div>,
    document.body,
  )
}
